import type { SimCareerView, SimMe } from '@cruz/shared';
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { navigate, usePath } from '../challenge/router.ts';
import { useTheme } from '../hooks/useTheme.ts';
import { AdminPage } from './Admin.tsx';
import { simApi, SimApiError, simErrorMessage } from './api.ts';
import {
  ForgotScreen,
  Landing,
  LoginScreen,
  RegisterScreen,
  ResetScreen,
  UnsubscribeScreen,
  VerifyScreen,
} from './Auth.tsx';
import { FoundScreen, IntroScreen } from './Found.tsx';
import { PrivacyPage, TermsPage } from './Legal.tsx';
import { parseSimRoute, type GameTab, type SimRoute } from './router.ts';
import { load, save } from '../lib/storage.ts';
import { JoinPage } from './Brothers.tsx';
import { DailyPage } from './Daily.tsx';
import { REF_KEY, SharePage } from './Share.tsx';
import { AchievementsTab, BrotherhoodTab, CharactersTab, LifeTab, SettingsTab } from './Tabs.tsx';
import { TodayTab } from './Today.tsx';
import { Notice, primaryButton, SimLayout, StatusBar, TabBar, Working } from './ui.tsx';

/** /simulador: «De cirio a hermano mayor». */

type Session =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'out' }
  | { status: 'in'; me: SimMe; career: SimCareerView | null };

const TITLES: Partial<Record<SimRoute['page'] | GameTab, string>> = {
  register: 'Hazte hermano',
  login: 'Entrar',
  forgot: 'Recuperar la contraseña',
  reset: 'Contraseña nueva',
  verify: 'Verificar el correo',
  unsubscribe: 'Darte de baja',
  privacy: 'Privacidad',
  terms: 'Condiciones',
  share: 'Un logro cofrade',
  daily: 'El tramo del día',
  join: 'Únete a la hermandad',
  admin: 'Panel de revisión',
  hermandad: 'Tu hermandad',
  vida: 'Mi vida cofrade',
  personajes: 'Personajes',
  logros: 'Logros',
  ajustes: 'Tu cuenta',
};

async function loadSession(): Promise<Session> {
  try {
    const me = await simApi.me();
    try {
      return { status: 'in', me, career: await simApi.career() };
    } catch (err) {
      if (err instanceof SimApiError && err.code === 'no_career')
        return { status: 'in', me, career: null };
      throw err;
    }
  } catch (err) {
    if (err instanceof SimApiError && err.status === 401) return { status: 'out' };
    return { status: 'error', message: simErrorMessage(err) };
  }
}

function Redirect({ to }: { to: string }) {
  useEffect(() => navigate(to), [to]);
  return null;
}

export function SimApp() {
  const theme = useTheme(null);
  const path = usePath();
  const route = parseSimRoute(path, location.search);
  const [session, setSession] = useState<Session>({ status: 'loading' });
  /** Just founded: show the brotherhood and its people before the first event */
  const [intro, setIntro] = useState(false);
  /** The career ended and the player starts another */
  const [founding, setFounding] = useState(false);

  const refresh = useCallback(async () => setSession(await loadSession()), []);
  useEffect(() => {
    let alive = true;
    void loadSession().then((s) => alive && setSession(s));
    return () => {
      alive = false;
    };
  }, []);

  const key = route.page === 'game' ? route.tab : route.page;
  useEffect(() => {
    const t = TITLES[key];
    document.title = t
      ? `${t} · De cirio a hermano mayor`
      : 'De cirio a hermano mayor · Cruz de IA';
  }, [key]);

  const signedIn = session.status === 'in';
  const setCareer = useCallback(
    (career: SimCareerView) => setSession((s) => (s.status === 'in' ? { ...s, career } : s)),
    [],
  );
  const setMe = (me: SimMe) => setSession((s) => (s.status === 'in' ? { ...s, me } : s));
  const signedInWith = (me: SimMe) => {
    setSession({ status: 'loading' });
    navigate('/simulador');
    void (async () => {
      const s = await loadSession();
      setSession(s.status === 'in' ? s : { status: 'in', me, career: null });
    })();
  };
  /** Play without an account: a guest session, then founding the brotherhood */
  const play = async () => {
    // Whoever opened a friend's card and starts, came from it
    const me = await simApi.guest(load<string | null>(REF_KEY, null));
    save(REF_KEY, null);
    signedInWith(me);
  };
  /** Joins a friend's brotherhood, as a guest if not signed in, and opens the career */
  const join = async (code: string) => {
    const me = session.status === 'in' ? session.me : await simApi.guest(null);
    await simApi.join(code);
    signedInWith(me);
  };
  const signedOut = () => {
    setSession({ status: 'out' });
    navigate('/simulador');
  };

  const layout = (children: ReactNode, tab?: string) => (
    <>
      <SimLayout theme={theme.mode} onTheme={theme.cycle} account={signedIn}>
        {children}
      </SimLayout>
      {tab && session.status === 'in' && session.career && (
        <TabBar current={tab} badge={tab !== 'hoy' && session.career.today.events_left > 0} />
      )}
    </>
  );

  // Pages that work signed in or not
  switch (route.page) {
    case 'privacy':
      return layout(<PrivacyPage />);
    case 'terms':
      return layout(<TermsPage />);
    case 'share':
      return layout(<SharePage id={route.id} onPlay={play} />);
    case 'join':
      return layout(<JoinPage code={route.code} onJoin={join} />);
    case 'daily':
      return layout(
        <DailyPage onPlay={play} hasCareer={session.status === 'in' && session.career !== null} />,
      );
    case 'admin':
      return layout(<AdminPage />);
    case 'verify':
      return layout(<VerifyScreen token={route.token} signedIn={signedIn} />);
    case 'unsubscribe':
      return layout(<UnsubscribeScreen unsubscribeKey={route.key} />);
    case 'reset':
      return layout(
        <ResetScreen
          token={route.token}
          onDone={() => void refresh().then(() => navigate('/simulador'))}
        />,
      );
    case 'not_found':
      return layout(
        <div className="space-y-4">
          <Notice tone="error">Esta página no existe.</Notice>
          <button type="button" className={primaryButton} onClick={() => navigate('/simulador')}>
            Ir al juego
          </button>
        </div>,
      );
  }

  if (session.status === 'loading') return layout(<Working label="Abriendo tu hermandad…" />);
  if (session.status === 'error') {
    return layout(
      <div className="space-y-3">
        <Notice tone="error">{session.message}</Notice>
        <button type="button" className={primaryButton} onClick={() => void refresh()}>
          Volver a probar
        </button>
      </div>,
    );
  }

  if (session.status === 'out') {
    switch (route.page) {
      case 'register':
        return layout(<RegisterScreen onDone={signedInWith} />);
      case 'login':
        return layout(<LoginScreen onDone={signedInWith} />);
      case 'forgot':
        return layout(<ForgotScreen />);
      default:
        if (route.page === 'game' && route.tab !== 'hoy') {
          return layout(
            <div className="space-y-4">
              <Notice>Entra en tu cuenta para ver tu partida.</Notice>
              <LoginScreen onDone={signedInWith} />
            </div>,
          );
        }
        return layout(<Landing onPlay={play} />);
    }
  }

  // Signed in
  const { me, career } = session;
  if (route.page !== 'game') {
    if (route.page === 'forgot') return layout(<ForgotScreen />);
    // A guest saves the career (sign up) or takes it to an account they had (log in)
    if (me.guest && route.page === 'register')
      return layout(<RegisterScreen guest onDone={signedInWith} />);
    if (me.guest && route.page === 'login')
      return layout(<LoginScreen guest onDone={signedInWith} />);
    // Register or login while signed in: straight to the game
    return <Redirect to="/simulador" />;
  }
  if (route.tab === 'ajustes') {
    return layout(
      <SettingsTab
        me={me}
        theme={theme.mode}
        onTheme={theme.cycle}
        onMe={setMe}
        onLogout={signedOut}
        onDeleted={signedOut}
      />,
      career && !intro ? 'ajustes' : undefined,
    );
  }
  if (!career || founding) {
    return layout(
      <FoundScreen
        again={Boolean(career)}
        onDone={(c) => {
          setCareer(c);
          setFounding(false);
          setIntro(true);
          navigate('/simulador');
          scrollTo({ top: 0 });
        }}
      />,
    );
  }
  if (intro) {
    return layout(
      <IntroScreen
        career={career}
        onStart={() => {
          setIntro(false);
          scrollTo({ top: 0 });
        }}
      />,
    );
  }

  const tabs: Record<Exclude<GameTab, 'ajustes'>, ReactNode> = {
    hoy: (
      <TodayTab
        me={me}
        career={career}
        onCareer={setCareer}
        onNewCareer={() => setFounding(true)}
      />
    ),
    hermandad: <BrotherhoodTab career={career} />,
    vida: <LifeTab career={career} />,
    personajes: <CharactersTab career={career} />,
    logros: <AchievementsTab career={career} />,
  };
  return layout(
    <>
      <StatusBar career={career} />
      {tabs[route.tab]}
    </>,
    route.tab,
  );
}
